import React, { useEffect, useMemo, type FC } from "react";
import { GrafosBuilder } from "../components/grafos/GrafosBuilder";
import { BahnPlot } from "../components/grafos/BahnPlot";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Tree,
} from "@focus/focusUI";
import { useTree } from "../hooks/useTree";
import { EvolutionPlot } from "../components/grafos/EvolutionPlot";

export interface IPlotItem<T = unknown> {
  id: number;
  plotType: string;
  dat?: T[];
}

const plotRenderers: Record<string, (plot: IPlotItem) => React.ReactNode> = {
  OPS_SPC: (plot) => <BahnPlot id={plot.id} />,
  LAT_LONG: (plot) => <BahnPlot id={plot.id} />,
  EVOLUTION_PLOT: (plot) => <EvolutionPlot id={plot.id} />,
  DEFAULT: (plot) => <GrafosBuilder id={plot.id} />,
};

interface PlotsContainerProps {
  plots: IPlotItem[]; // array de objetos que contienen plots
  context?: string; // opcional, para keys únicas
  availableConjHeaders?: any; // opcional, para CAMPanel
  camPlotId?: string; // por defecto "CAM"
  renderCustomPlot?: (plot: IPlotItem) => React.ReactNode; // para renderizar plots personalizados
}

export const PlotItem: FC<{
  item: IPlotItem;
  renderCustomPlot?: (plot: IPlotItem) => React.ReactNode;
}> = ({ item, renderCustomPlot }) => {
  if (renderCustomPlot) return <>{renderCustomPlot(item)}</>;

  const renderer = plotRenderers[item.plotType] || plotRenderers.DEFAULT;

  return <div className="focusoc-plot h-full">{renderer(item)}</div>;
};

export const PlotsContainer: React.FC<PlotsContainerProps> = ({
  plots,
  renderCustomPlot,
}) => {
  const { selected, selectNode, setInitialSelected } = useTree<IPlotItem>();
  const groupedPlots = useMemo(() => {
    if (!plots?.length) return new Map<string, IPlotItem[]>();

    return plots.reduce((acc, plot) => {
      if (!acc.has(plot.plotType)) {
        acc.set(plot.plotType, []);
      }
      acc.get(plot.plotType)!.push(plot);
      return acc;
    }, new Map<string, IPlotItem[]>());
  }, [plots]);

  const sortedTypes = useMemo(() => {
    return Array.from(groupedPlots.keys()).sort();
  }, [groupedPlots]);

  const tree = useMemo(() => {
    return sortedTypes.map((type, i) => {
      const plot = groupedPlots.get(type)![0];

      return {
        id: plot.id,
        label: type,
        nodeData: plot,
        isSelected: selected?.id === plot.id,
      };
    });
  }, [sortedTypes, groupedPlots, selected]);

  useEffect(() => {
    if (sortedTypes.length > 0) {
      const firstPlot = groupedPlots.get(sortedTypes[0])?.[0];
      if (firstPlot) {
        setInitialSelected(firstPlot);
      }
    }
  }, [sortedTypes, groupedPlots, setInitialSelected]);

  return (
    <ResizablePanelGroup direction="horizontal" className="flex flex-1">
      <ResizablePanel minSize={10} defaultSize={20} maxSize={35}>
        <Tree contents={tree} onNodeClick={selectNode} />
      </ResizablePanel>

      <ResizableHandle disabled />

      <ResizablePanel>
        {selected && (
          <div className="flex flex-col w-full h-full">
            <PlotItem item={selected} renderCustomPlot={renderCustomPlot} />
          </div>
        )}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
